<template>
  <ZapiModalShell
    :visible="show"
    :title="data_trabajador.id ? 'Editar Trabajador' : 'Nuevo Trabajador'"
    :loading="enviando"
    @update:visible="handleClose"
    @confirm="saveTrabajador"
  >
    <div class="p-fluid formgrid grid">
      <!-- NOMBRE COMPLETO: ocupa todo el ancho -->
      <div class="field col-12">
        <label for="nombre_completo">
          <strong>NOMBRE COMPLETO</strong> 
          <span class="p-invalid">*</span>
        </label>
        <InputText
          id="nombre_completo"
          v-model.trim="data_trabajador.nombre_completo"
          required="true"
          autofocus
          :class="{ 'p-invalid': errors.nombre_completo }"
        />
        <small class="p-error" v-if="errors.nombre_completo">
          <i class="pi pi-exclamation-circle"></i> 
          {{ errors.nombre_completo[0] }}
        </small>
      </div>

      <!-- CARNET DE IDENTIDAD: ocupa todo el ancho -->
      <div class="field col-12">
        <label for="carnet_identidad">
          <strong>CARNET DE IDENTIDAD</strong> 
          <span class="p-invalid">*</span>
        </label>
        <InputText
          id="carnet_identidad"
          v-model.trim="data_trabajador.carnet_identidad"
          required="true"
          :class="{ 'p-invalid': errors.carnet_identidad }"
        />
        <small class="p-error" v-if="errors.carnet_identidad">
          <i class="pi pi-exclamation-circle"></i> 
          {{ errors.carnet_identidad[0] }}
        </small>
      </div>

      <!-- CELULAR Y DIRECCIÓN: en una fila de 2 columnas -->
      <div class="field col-12 md:col-6">
        <label for="celular">
          <strong>CELULAR</strong>
        </label>
        <InputText
          id="celular"
          v-model.trim="data_trabajador.celular"
          :class="{ 'p-invalid': errors.celular }"
        />
        <small class="p-error" v-if="errors.celular">
          <i class="pi pi-exclamation-circle"></i> 
          {{ errors.celular[0] }}
        </small>
      </div>

      <div class="field col-12 md:col-6">
        <label for="direccion">
          <strong>DIRECCIÓN</strong>
        </label>
        <InputText
          id="direccion"
          v-model.trim="data_trabajador.direccion"
          :class="{ 'p-invalid': errors.direccion }"
        />
        <small class="p-error" v-if="errors.direccion">
          <i class="pi pi-exclamation-circle"></i> 
          {{ errors.direccion[0] }}
        </small>
      </div>

      <!-- FECHAS: en una fila de 2 columnas -->
      <div class="field col-12 md:col-6">
        <label for="fecha_nacimiento">
          <strong>FECHA DE NACIMIENTO</strong>
        </label>
        <Calendar
          id="fecha_nacimiento"
          v-model="data_trabajador.fecha_nacimiento"
          dateFormat="yy-mm-dd"
          :showIcon="true"
          :class="{ 'p-invalid': errors.fecha_nacimiento }"
          placeholder="Seleccione una fecha"
        />
        <small class="p-error" v-if="errors.fecha_nacimiento">
          <i class="pi pi-exclamation-circle"></i> 
          {{ errors.fecha_nacimiento[0] }}
        </small>
      </div>

      <div class="field col-12 md:col-6">
        <label for="fecha_contratacion">
          <strong>FECHA DE CONTRATACIÓN</strong>
        </label>
        <Calendar
          id="fecha_contratacion"
          v-model="data_trabajador.fecha_contratacion"
          dateFormat="yy-mm-dd"
          :showIcon="true"
          :class="{ 'p-invalid': errors.fecha_contratacion }"
          placeholder="Seleccione una fecha"
        />
        <small class="p-error" v-if="errors.fecha_contratacion">
          <i class="pi pi-exclamation-circle"></i> 
          {{ errors.fecha_contratacion[0] }}
        </small>
      </div>

      <!-- ESTADO (solo para edición) -->
      <div class="field col-12 md:col-6" v-if="data_trabajador.id">
        <label for="estado">
          <strong>ESTADO</strong> 
          <span class="p-invalid">*</span>
        </label>
        <Dropdown
          id="estado"
          v-model="data_trabajador.estado"
          :options="estados"
          optionLabel="label"
          placeholder="Seleccione un estado..."
          :class="{ 'p-invalid': errors.estado }"
          showClear
        >
          <template #value="slotProps">
            <span v-if="slotProps.value" :class="'product-badge ' + (slotProps.value.value ? 'status-instock' : 'status-outofstock')">
              {{ slotProps.value.label }}
            </span>
          </template>
        </Dropdown>
        <small class="p-error" v-if="errors.estado">
          <i class="pi pi-exclamation-circle"></i> 
          {{ errors.estado[0] }}
        </small>
      </div>

      <!-- Indicador de campos requeridos -->
      <div class="field col-12 mt-4">
        <div class="required-indicator">
          <span class="p-invalid">*</span> 
          <span class="ml-1">Campos requeridos</span>
        </div>
      </div>
    </div>
  </ZapiModalShell>
</template>

<script>
import TrabajadorService from "@/service/TrabajadorService";
import ZapiModalShell from "@/components/shared/ZapiModalShell.vue";

export default {
  components: { ZapiModalShell },
  emits: ["closeModal", "actualizarListado"],
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    trabajador: {
      type: Object,
      default() {
        return {};
      },
    },
  },
  data() {
    return {
      data_trabajador: {},
      enviando: false,
      estados: [
        { label: "Inactivo", value: 0 },
        { label: "Activo", value: 1 },
      ],
      errors: {},
    };
  },
  trabajadorService: null,
  created() {
    this.trabajadorService = new TrabajadorService();
  },
  methods: {
    handleClose(value) {
      if (!value) {
        this.errors = {};
        this.$emit("closeModal");
      }
    },
    formatDateForApi(dateObj) {
      if (!dateObj) return null;
      if (typeof dateObj === 'string') return dateObj;
      const d = new Date(dateObj);
      if (isNaN(d)) return null;
      let month = '' + (d.getMonth() + 1);
      let day = '' + d.getDate();
      const year = d.getFullYear();
      if (month.length < 2) month = '0' + month;
      if (day.length < 2) day = '0' + day;
      return [year, month, day].join('-');
    },
    saveTrabajador() {
      this.errors = {};
      this.enviando = true;

      let payload = {
        ...this.data_trabajador,
        fecha_nacimiento: this.formatDateForApi(this.data_trabajador.fecha_nacimiento),
        fecha_contratacion: this.formatDateForApi(this.data_trabajador.fecha_contratacion),
      };

      if (payload.estado && typeof payload.estado === 'object') {
        payload.estado = payload.estado.value;
      }

      if (this.data_trabajador.id) {
        this.trabajadorService.updateTrabajador(this.data_trabajador.id, payload).then((data) => {
          if (Object.prototype.hasOwnProperty.call(data, "errors")) {
            this.errors = data.errors;
            this.enviando = false;
          } else {
            this.$toast.add({
              severity: "success",
              summary: "Exito!",
              detail: data.mensaje || "Trabajador actualizado",
              life: 3000,
            });
            this.enviando = false;
            this.$emit("actualizarListado");
            this.$emit("closeModal");
          }
        }).catch(err => {
            this.enviando = false;
            if(err && err.response && err.response.data && err.response.data.errors) {
                this.errors = err.response.data.errors;
            }
        });
      } else {
        this.trabajadorService.createTrabajador(payload).then((data) => {
          if (Object.prototype.hasOwnProperty.call(data, "errors")) {
            this.errors = data.errors;
            this.enviando = false;
          } else {
            this.$toast.add({
              severity: "success",
              summary: "Exito!",
              detail: data.mensaje || "Trabajador creado",
              life: 3000,
            });
            this.enviando = false;
            this.$emit("actualizarListado");
            this.$emit("closeModal");
          }
        }).catch(err => {
            this.enviando = false;
            if(err && err.response && err.response.data && err.response.data.errors) {
                this.errors = err.response.data.errors;
            }
        });
      }
    },
  },
  watch: {
    trabajador: {
      handler(val) {
        this.data_trabajador = { ...val };
        if (this.data_trabajador.id && this.data_trabajador.estado !== undefined && typeof this.data_trabajador.estado !== 'object') {
          this.data_trabajador.estado = this.estados.find(e => e.value == this.data_trabajador.estado);
        }
        if (this.data_trabajador.fecha_nacimiento && typeof this.data_trabajador.fecha_nacimiento === 'string') {
          this.data_trabajador.fecha_nacimiento = new Date(this.data_trabajador.fecha_nacimiento);
        }
        if (this.data_trabajador.fecha_contratacion && typeof this.data_trabajador.fecha_contratacion === 'string') {
          this.data_trabajador.fecha_contratacion = new Date(this.data_trabajador.fecha_contratacion);
        }
      },
      immediate: true,
      deep: true
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/demo/badges.scss";

.p-invalid {
  color: red;
}

.p-error {
  color: #f44336;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: 0.25rem;

  i {
    font-size: 0.7rem;
  }
}

.required-indicator {
  font-size: 0.85rem;
  color: #6c757d;
  display: flex;
  align-items: center;
  padding: 0.5rem;
  background-color: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e9ecef;

  .p-invalid {
    font-size: 1rem;
  }
}

.field {
  :deep(.p-inputtext) {
    width: 100%;
    
    &.p-invalid {
      border-color: #f44336;
    }
  }

  :deep(.p-calendar) {
    width: 100%;
    
    .p-inputtext {
      width: 100%;
    }
  }

  :deep(.p-dropdown) {
    width: 100%;
    
    &.p-invalid {
      border-color: #f44336;
    }
  }
}

.mt-4 {
  margin-top: 1rem;
}

.ml-1 {
  margin-left: 0.25rem;
}
</style>